import { useState, useEffect, useContext, useCallback } from 'react';
import { registerUpload } from '@/api';
import { Modal, UserContext } from '@/components';
import { getIcon } from '@/utils/scrapbookUtils';
import XLight from '@/assets/xLight.svg';

interface UploadFile {
  name: string;
  sas: string;
  fileItem: File;
  status: 'waiting' | 'uploading' | 'complete' | 'error' | string;
  bytesUploaded: number;
}

interface UploadFrameProps {
  files: UploadFile[];
  currentFolder: { id: number; path: string };
  open: boolean;
  onClose: () => void;
}

export default function UploadFrame({ files, currentFolder, open, onClose }: UploadFrameProps) {
  const user = useContext(UserContext);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>(files);
  const [title, setTitle] = useState('Uploading files, please wait.');
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<AbortController | null>(null);

  const formatBytes = (bytes: number, decimals = 2) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const startUpload = useCallback(
    async (file: UploadFile) => {
      const controller = new AbortController();
      setCurrentRequest(controller);

      setUploadFiles((prev) => prev.map((f) => (f.name === file.name ? { ...f, status: 'uploading' as const } : f)));

      const uploadComplete = async (completedFile: UploadFile) => {
        // Register upload
        try {
          const result = await registerUpload(
            user?.userId || '',
            currentFolder.path,
            currentFolder.id,
            completedFile.name,
            completedFile.fileItem.size,
            completedFile.fileItem.type,
          );

          if (!result.succeeded) {
            setUploadFiles((prev) => {
              const updated = prev.map((f) =>
                f.name === completedFile.name ? { ...f, status: result.message || 'error' } : f,
              );
              const nextFile = updated.find((f) => f.status === 'waiting');
              if (nextFile) {
                setTimeout(() => startUpload(nextFile), 0);
              } else {
                setTimeout(() => {
                  setTitle('Upload Complete');
                  setShowCloseButton(true);
                }, 100);
              }
              return updated;
            });
            return;
          }
        } catch (error) {
          console.error('Register upload error:', error);
          return;
        }

        setUploadFiles((prev) => {
          const updated = prev.map((f) => (f.name === completedFile.name ? { ...f, status: 'complete' as const } : f));

          // Find next file to upload from the updated state
          const nextFile = updated.find((f) => f.status === 'waiting');
          if (nextFile) {
            // Use setTimeout to avoid state update conflicts
            setTimeout(() => startUpload(nextFile), 0);
          } else {
            // All uploads complete - update title and show close button
            setTimeout(() => {
              setTitle('Upload Complete');
              setShowCloseButton(true);
            }, 100);
          }

          return updated;
        });

        setCurrentRequest(null);
      };

      try {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            setUploadFiles((prev) =>
              prev.map((f) => (f.name === file.name ? { ...f, bytesUploaded: event.loaded } : f)),
            );
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            uploadComplete(file);
          } else {
            setUploadFiles((prev) => {
              const updated = prev.map((f) => (f.name === file.name ? { ...f, status: 'error' } : f));
              const nextFile = updated.find((f) => f.status === 'waiting');
              if (nextFile) {
                setTimeout(() => startUpload(nextFile), 0);
              } else {
                setTimeout(() => {
                  setTitle('Upload Complete');
                  setShowCloseButton(true);
                }, 100);
              }
              return updated;
            });
          }
        });

        xhr.addEventListener('error', () => {
          setUploadFiles((prev) => {
            const updated = prev.map((f) => (f.name === file.name ? { ...f, status: 'error' } : f));
            const nextFile = updated.find((f) => f.status === 'waiting');
            if (nextFile) {
              setTimeout(() => startUpload(nextFile), 0);
            } else {
              setTimeout(() => {
                setTitle('Upload Complete');
                setShowCloseButton(true);
              }, 100);
            }
            return updated;
          });
        });

        controller.signal.addEventListener('abort', () => {
          xhr.abort();
        });

        xhr.open('PUT', file.sas);
        xhr.setRequestHeader('Content-Type', file.fileItem.type);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.send(file.fileItem);
      } catch (error) {
        console.error('Upload error:', error);
        setUploadFiles((prev) => prev.map((f) => (f.name === file.name ? { ...f, status: 'error' as const } : f)));
      }
    },
    [user?.userId, currentFolder.path, currentFolder.id],
  );

  useEffect(() => {
    if (uploadFiles.length > 0 && open) {
      setTitle('Uploading files, please wait.');
      setShowCloseButton(false);
      const firstFile = uploadFiles.find((f) => f.status === 'waiting');
      if (firstFile) {
        startUpload(firstFile);
      }
    }
  }, [uploadFiles, startUpload, open]);

  useEffect(() => {
    setUploadFiles(files);
  }, [files]);

  const cancelAll = () => {
    if (currentRequest) {
      currentRequest.abort();
    }
    setUploadFiles([]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={cancelAll}
      disableCloseOnBackdropClick
      contanerClassName="w-[550px] max-w-[90vw] max-h-[90vh] overflow-hidden bg-white shadow-xl"
    >
      <div className="z-50 flex max-h-[80vh] flex-col rounded-md border border-gray-300 bg-gray-50 shadow-sm">
        <div className="flex flex-shrink-0 items-center justify-between p-4">
          <h2 className="text-xl font-bold">{title}</h2>

          <XLight className="h-7 w-7 cursor-pointer rounded hover:bg-gray-100" onClick={cancelAll} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto" data-lenis-prevent>
          <div className="p-3">
            {uploadFiles.map((file, index) => (
              <div key={index} className="mb-2 w-full rounded border border-gray-300 p-4">
                <h4 className="mb-2 flex items-center text-lg font-bold">
                  {(() => {
                    const IconComponent = getIcon(file.fileItem.type);
                    return <IconComponent className="mr-2 h-6 w-6" />;
                  })()}
                  {file.name}
                </h4>

                <div className="mb-2">
                  Status: <span className="capitalize">{file.status}</span>
                </div>

                <div className="mb-2">
                  <div className="h-2 w-full rounded bg-gray-200">
                    <div
                      className={`h-full rounded transition-all duration-300 ${
                        file.status === 'uploading' ? 'animate-pulse bg-black' : 'bg-black'
                      }`}
                      style={{
                        width: `${(file.bytesUploaded * 100) / file.fileItem.size}%`,
                      }}
                    />
                  </div>
                </div>

                <div>
                  Uploaded: {formatBytes(file.bytesUploaded)} of {formatBytes(file.fileItem.size)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {showCloseButton && (
          <div className="flex-shrink-0 p-3">
            <button
              onClick={cancelAll}
              className="cursor-pointer rounded-3xl bg-black px-6 py-2 text-white hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
